import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Button,
  Form,
  Alert,
  Spinner,
  Modal,
  Row,
  Col,
  Card,
  Badge,
} from "react-bootstrap";
import { getDownloadURL, listAll, ref } from "firebase/storage";
import { addLog, storage } from "../../firebase";
import {
  faFile,
  faFolder,
  faFolderOpen,
  faHome,
  faSearch,
  faCloudArrowDown,
  faArrowRight,
  faXmark,
  faFolderPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../firebaseContext";
import FileIndexSearch from "./FileIndexSearch"; // Adjust path if needed

export default function DownloadsPage() {
  const listPath = "Downloadable Files";
  const listRef = useMemo(() => ref(storage, listPath), [listPath]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser, admin } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [showSidebar, setShowSidebar] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchDownloads = async () => {
      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
          setFolders([]);
          setFiles([]);
        }

        const res = await listAll(listRef);

        if (!isMounted) return;

        setFolders(
          res.prefixes.map((folderRef) => [folderRef.name, folderRef])
        );

        const loadedFiles = await Promise.all(
          res.items.map(async (itemRef) => [
            itemRef.name,
            await getDownloadURL(itemRef),
            itemRef.parent,
          ])
        );

        if (!isMounted) return;

        setFiles(loadedFiles);
      } catch (err) {
        if (isMounted) {
          console.error("Unable to load downloadable files", err);
          setError(
            "We couldn't load the downloads library right now. Please refresh or try again shortly."
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDownloads();

    return () => {
      isMounted = false;
    };
  }, [listRef]);

  function handleSelectFolder(folderRef) {
    navigate("/folder/" + folderRef.name, {
      state: { folder: folderRef.fullPath },
    });
  }

  function handleDownloadButton(filename, fileURL) {
    if (currentUser?.email) {
      addLog(currentUser.email, filename, "Downloaded");
    }
    const anchor = document.createElement("a");
    anchor.href = fileURL;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  function sortFunction(a, b) {
    return a[0].toLowerCase().localeCompare(b[0].toLowerCase());
  }

  function handleAdminButton() {
    navigate("/addFolder", {
      state: { path: listRef.fullPath, type: "files" },
    });
  }

  function getFileExtension(filename) {
    const parts = filename.split(".");
    return parts.length > 1 ? parts.pop().toUpperCase() : "FILE";
  }

  const getLocationLabel = useCallback(
    (parentRef) => {
      if (!parentRef?.fullPath) {
        return "Root library";
      }

      const relativePath = parentRef.fullPath
        .replace(listRef.fullPath, "")
        .replace(/^\//, "");
      return relativePath.length > 0 ? relativePath : "Root library";
    },
    [listRef]
  );

  const sortedFolders = useMemo(
    () => [...folders].sort(sortFunction),
    [folders]
  );

  const sortedFiles = useMemo(() => [...files].sort(sortFunction), [files]);

  const filteredFiles = useMemo(() => {
    if (!searchTerm) {
      return sortedFiles;
    }
    const query = searchTerm.toLowerCase();
    return sortedFiles.filter((file) => file[0].toLowerCase().includes(query));
  }, [sortedFiles, searchTerm]);

  const enhancedFiles = useMemo(
    () =>
      filteredFiles.map(([name, url, parentRef]) => ({
        name,
        url,
        extension: getFileExtension(name),
        location: getLocationLabel(parentRef),
      })),
    [filteredFiles, getLocationLabel]
  );

  // User-friendly address
  const userFriendlyAddress = "Downloadable Forms";
  const totalFolders = folders.length;
  const totalFiles = files.length;

  return (
    <div className="downloads-page">
      <section className="downloads-hero-wrapper py-5">
        <Container fluid="lg">
          <div className="downloads-hero">
            <Row className="align-items-center gy-4">
              <Col lg={7} className="downloads-hero-content">
                <div className="downloads-hero-badge">Resource library</div>
                <h1 className="downloads-hero-title">{userFriendlyAddress}</h1>
                <p className="downloads-hero-subtitle">
                  Discover the latest templates, forms, and guides curated for
                  the Nobility Care Australia team. Browse folders or jump
                  straight to what you need with lightning-fast search.
                </p>
                <div className="downloads-hero-actions">
                  <Button
                    variant="light"
                    className="downloads-hero-action"
                    onClick={() => setShowSidebar(true)}
                  >
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    Quick search
                  </Button>
                  <Button
                    variant="outline-light"
                    className="downloads-hero-action"
                    onClick={() => navigate("/")}
                  >
                    <FontAwesomeIcon icon={faHome} className="me-2" />
                    Back to dashboard
                  </Button>
                  {admin && (
                    <Button
                      variant="light"
                      className="downloads-hero-action"
                      onClick={handleAdminButton}
                    >
                      <FontAwesomeIcon icon={faFolderPlus} className="me-2" />
                      Add folder & files
                    </Button>
                  )}
                </div>
              </Col>
              <Col lg={5}>
                <Card className="downloads-hero-card h-100">
                  <Card.Body>
                    <Row className="g-3">
                      <Col xs={12} sm={6}>
                        <div className="downloads-stat-card">
                          <div className="downloads-stat-icon downloads-stat-icon-folders">
                            <FontAwesomeIcon icon={faFolderOpen} />
                          </div>
                          <div>
                            <div className="downloads-stat-value">
                              {totalFolders}
                            </div>
                            <div className="downloads-stat-label">
                              Active folders
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col xs={12} sm={6}>
                        <div className="downloads-stat-card">
                          <div className="downloads-stat-icon downloads-stat-icon-files">
                            <FontAwesomeIcon icon={faFile} />
                          </div>
                          <div>
                            <div className="downloads-stat-value">
                              {totalFiles}
                            </div>
                            <div className="downloads-stat-label">
                              Available files
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col xs={12}>
                        <div className="downloads-tip-card">
                          <h3 className="downloads-tip-title">
                            Need a shortcut?
                          </h3>
                          <p className="downloads-tip-copy">
                            Use the quick search panel to instantly jump to the
                            right resource. Filter by title, folder, or keyword
                            without leaving this page.
                          </p>
                          <Button
                            variant="outline-primary"
                            className="downloads-tip-btn"
                            onClick={() => setShowSidebar(true)}
                          >
                            Launch quick search
                            <FontAwesomeIcon
                              icon={faArrowRight}
                              className="ms-2"
                            />
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      <Container fluid="lg" className="downloads-content pb-5">
        {error && (
          <Alert variant="danger" className="downloads-alert">
            {error}
          </Alert>
        )}

        <section className="downloads-section mb-4">
          <div className="downloads-section-header">
            <div>
              <h2 className="downloads-section-title">Folder library</h2>
              <p className="downloads-section-subtitle">
                Organised hubs for the most-used tools across the business. Open
                a folder to explore its dedicated resources.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="downloads-loading-panel">
              <Spinner animation="border" role="status" />
            </div>
          ) : totalFolders > 0 ? (
            <Row className="g-4">
              {sortedFolders.map(([folderName, folderRef]) => (
                <Col key={folderRef.fullPath} xs={12} md={6} xl={4}>
                  <Card
                    className="downloads-folder-card h-100"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelectFolder(folderRef)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleSelectFolder(folderRef);
                      }
                    }}
                  >
                    <Card.Body className="downloads-folder-body">
                      <div className="downloads-folder-icon">
                        <FontAwesomeIcon icon={faFolder} />
                      </div>
                      <h3
                        className="downloads-folder-name text-truncate mb-0"
                        title={folderName}
                      >
                        {folderName}
                      </h3>
                      <div className="downloads-folder-arrow">
                        <FontAwesomeIcon icon={faArrowRight} />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Alert variant="light" className="downloads-empty">
              No folders are available yet. Check back soon for curated bundles
              of resources.
            </Alert>
          )}
        </section>

        <section className="downloads-section">
          <div className="downloads-section-header flex-column flex-lg-row align-items-lg-center">
            <div>
              <h2 className="downloads-section-title">
                All downloadable files
              </h2>
              <p className="downloads-section-subtitle">
                Search by name or filter through the gallery to find the exact
                document you need.
              </p>
            </div>
            <Form
              className="downloads-search-form mt-3 mt-lg-0"
              onSubmit={(event) => event.preventDefault()}
            >
              <div className="downloads-search-input">
                <FontAwesomeIcon icon={faSearch} />
                <Form.Control
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search by file name..."
                />
                {searchTerm && (
                  <Button
                    variant="link"
                    className="downloads-search-clear"
                    onClick={() => setSearchTerm("")}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                    <span className="visually-hidden">Clear search</span>
                  </Button>
                )}
              </div>
            </Form>
          </div>

          {loading ? (
            <div className="downloads-loading-panel">
              <Spinner animation="border" role="status" />
            </div>
          ) : enhancedFiles.length > 0 ? (
            <Row className="g-4">
              {enhancedFiles.map((file) => (
                <Col key={file.url} xs={12} md={6} xl={4}>
                  <Card className="downloads-file-card h-100">
                    <Card.Body>
                      <div className="downloads-file-head">
                        <div className="downloads-file-icon">
                          <FontAwesomeIcon icon={faFile} />
                        </div>
                        <Badge className="downloads-file-badge">
                          {file.extension}
                        </Badge>
                      </div>
                      <h3 className="downloads-file-name" title={file.name}>
                        {file.name}
                      </h3>
                      <p
                        className="downloads-file-location"
                        title={file.location}
                      >
                        {file.location}
                      </p>
                    </Card.Body>
                    <Card.Footer>
                      <Button
                        variant="primary"
                        className="downloads-file-btn"
                        onClick={() =>
                          handleDownloadButton(file.name, file.url)
                        }
                        title={`Download ${file.name}`}
                      >
                        <FontAwesomeIcon
                          icon={faCloudArrowDown}
                          className="me-2"
                        />
                        Download
                      </Button>
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Alert variant="light" className="downloads-empty">
              {searchTerm
                ? "No files match your search right now. Try another keyword or clear the filter."
                : "There are no downloadable files available in this library yet."}
            </Alert>
          )}
        </section>
      </Container>

      <Modal
        show={showSidebar}
        onHide={() => setShowSidebar(false)}
        centered
        size="lg"
        fullscreen="md-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>Quick file search</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FileIndexSearch />
        </Modal.Body>
      </Modal>
    </div>
  );
}
